import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { UserProfile } from '@covered-calls/shared';
import { getProfile } from '../../services/profile-service';
import { OnboardingPrompt } from './OnboardingPrompt';

/**
 * Budget for the post-sign-in profile fetch (Decision 7). If the profile is not
 * resolved within this window we abandon the decision and render the app
 * unhindered (Req 1.8) — the prompt is never shown on a slow/failed fetch.
 */
const PROFILE_FETCH_BUDGET_MS = 3_000;

/** A rejection sentinel used by the timeout leg of the fetch race. */
const TIMEOUT = Symbol('onboarding-gate-timeout');

/**
 * Resolve after `ms`, then reject with the {@link TIMEOUT} sentinel. Used as the
 * losing leg of a `Promise.race` so the gate never waits longer than the budget
 * for `getProfile()`.
 */
function rejectAfter(ms: number): Promise<never> {
  return new Promise((_resolve, reject) => {
    setTimeout(() => reject(TIMEOUT), ms);
  });
}

/**
 * Post-sign-in onboarding gate (Component 8).
 *
 * Mounted inside the authenticated app tree, so a session is already
 * established when it runs. On mount it fetches the profile within a 3-second
 * budget and decides whether to overlay {@link OnboardingPrompt}:
 *
 * - Shown **iff** the fetch succeeded, `onboardingState === 'NOT_STARTED'`, and
 *   the in-memory `skippedThisSession` flag is false (Req 1.1, 1.4, 1.5).
 * - Suppressed for `COMPLETED` / `DISMISSED` (terminal states never re-prompt;
 *   Req 1.4, 1.5).
 * - Suppressed on fetch failure or timeout, leaving state unchanged (Req 1.8).
 *
 * The gate always renders `{children}` so navigation is never blocked
 * (Req 1.3); the prompt is a fixed overlay on top when shown.
 *
 * `skippedThisSession` lives in component state, so a fresh page load (a new
 * session) resets it and the prompt reappears while state is `NOT_STARTED`
 * (Req 5.2, 5.3, Decision 7).
 *
 * Validates: Requirements 1.1, 1.3, 1.4, 1.5, 1.6, 1.8, 5.2, 5.3, 5.7.
 */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // True once the fetch has resolved and we may act on `profile`.
  const [decided, setDecided] = useState(false);
  // The prompt was completed/dismissed locally for this session — stop showing.
  const [resolvedLocally, setResolvedLocally] = useState(false);
  // Session-only skip flag (Req 5.2, 5.3); resets on a fresh load.
  const skippedThisSessionRef = useRef(false);
  const [skippedThisSession, setSkippedThisSession] = useState(false);

  useEffect(() => {
    let active = true;

    // Race the profile fetch against the 3-second budget. On timeout or any
    // error we simply mark the fetch decided without a profile, so the prompt
    // is never shown and the app continues (Req 1.8).
    Promise.race([getProfile(), rejectAfter(PROFILE_FETCH_BUDGET_MS)])
      .then((result) => {
        if (!active) {
          return;
        }
        setProfile(result);
      })
      .catch(() => {
        // Timeout or fetch error — leave `profile` null and render children.
      })
      .finally(() => {
        if (active) {
          setDecided(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  /** Hide the prompt permanently for this session after a successful save. */
  const handleComplete = () => {
    setResolvedLocally(true);
  };

  /** Session-only skip: never touches server state (Req 5.2). */
  const handleSkip = () => {
    skippedThisSessionRef.current = true;
    setSkippedThisSession(true);
  };

  const showPrompt =
    decided &&
    !resolvedLocally &&
    !skippedThisSession &&
    !skippedThisSessionRef.current &&
    profile !== null &&
    profile.onboardingState === 'NOT_STARTED';

  return (
    <>
      {children}
      {showPrompt && profile !== null && (
        <OnboardingPrompt
          initialProfile={profile}
          onComplete={handleComplete}
          onSkip={handleSkip}
        />
      )}
    </>
  );
}
